import { ApiErrorCode } from '@orn/global/errors/apiErrorCodes.js';

import { BillStatus } from '../../__generated__/globalTypes';
import { extractErrorCode } from '../../utils/apollo/error';
import { getBillStatusPill, type ITimingPill } from '../../utils/records/timingPill';

import type { RecordPaneStatus } from '../../components/entity/RecordPane';
import type { ApolloError } from '@apollo/client';

export interface ITransactionPageRead {
  loading: boolean;
  error: ApolloError | undefined;
  /** Whether the read has a transaction to draw. */
  hasTransaction: boolean;
}

/**
 * Which shell state the page is in. `getTransaction` is non-nullable on the
 * wire — a missing or hidden transaction comes back as a coded GraphQL error,
 * not a null — so not-found is read off the error code. A read that never ran
 * (no id) is not-found too: the shell's copy covers it.
 */
export function resolveTransactionPageStatus({
  loading,
  error,
  hasTransaction,
}: ITransactionPageRead): RecordPaneStatus {
  if (hasTransaction) {
    return 'ready';
  }
  if (loading) {
    return 'loading';
  }
  if (error) {
    return isTransactionNotFound(error) ? 'not-found' : 'error';
  }
  return 'not-found';
}

export interface ITransactionPaymentLinks {
  isPending: boolean;
  bill?: { status: BillStatus } | null;
  recurringTransaction?: object | null;
}

/**
 * The hero's status pill: a bill payment shows its bill's settled status, and
 * a payment on a recurring stream shows Paid. A pending transaction has not
 * paid anything yet, so it shows none.
 */
export function resolveTransactionStatusPill({
  isPending,
  bill,
  recurringTransaction,
}: ITransactionPaymentLinks): ITimingPill | null {
  if (isPending) {
    return null;
  }
  if (bill) {
    return getBillStatusPill(bill.status);
  }
  return recurringTransaction ? getBillStatusPill(BillStatus.Paid) : null;
}

function isTransactionNotFound(error: ApolloError): boolean {
  return error.graphQLErrors.length > 0 && extractErrorCode(error.graphQLErrors) === ApiErrorCode.TRANSACTION_NOT_FOUND;
}
